import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  isPushSupported,
  isStandalonePWA,
  getCurrentPermission,
  getActiveSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';

/**
 * Compact "Enable home-screen notifications" button.
 * Adapts to context:
 *   - Desktop / mobile-browser (not installed PWA) → "Install on phone" hint
 *   - PWA + blocked → "Blocked, enable in Settings"
 *   - PWA + default/granted-not-subscribed → "Enable" button
 *   - PWA + subscribed → "Enabled"
 */
const EnableNotificationsButton = ({ allowBrowser = false }) => {
  const [supported] = useState(() => isPushSupported());
  const [installed] = useState(() => isStandalonePWA());
  const standalone = installed || allowBrowser;
  const inBrowser = allowBrowser && !installed;
  const [permission, setPermission] = useState(() => getCurrentPermission());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported || !standalone) return;
    (async () => {
      const sub = await getActiveSubscription();
      setIsSubscribed(!!sub);
    })();
  }, [supported, standalone]);

  if (!supported) {
    return (
      <p className="text-xs text-stone-500" data-testid="push-unsupported">
        Notifications are not supported in this browser. Open the app from your iOS or Android home screen to enable them.
      </p>
    );
  }

  // The most common confusing case: user is on the regular Safari/Chrome browser,
  // not the installed PWA. Don't show "blocked" — show a helpful hint instead.
  if (!standalone) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid="push-not-installed">
        <p className="text-sm text-stone-900 flex items-center gap-2">
          <Smartphone size={16} className="text-[#EFB342]" />
          Home-screen alerts work in the installed app
        </p>
        <p className="text-xs text-stone-600 mt-1">
          On your phone, tap Share → <strong>Add to Home Screen</strong>, then open RxGRIT from that icon to turn on alerts.
        </p>
      </div>
    );
  }

  const handleToggle = async () => {
    setBusy(true);
    try {
      if (isSubscribed) {
        await unsubscribeFromPush();
        setIsSubscribed(false);
        toast.success('Notifications turned off');
      } else {
        await subscribeToPush();
        setIsSubscribed(true);
        setPermission('granted');
        toast.success(inBrowser ? 'Pop-up alerts are on for this browser' : 'Notifications enabled — you\'ll see a red dot on your icon for new updates');
      }
    } catch (err) {
      const current = getCurrentPermission();
      const msg = inBrowser && current !== 'granted'
        ? 'Your browser didn\'t allow notifications. Click the icon at the left of the web address (lock or bell) → Notifications → Allow, then refresh and try again.'
        : (err?.message || 'Could not change notification settings');
      toast.error(msg, { duration: 10000 });
      setPermission(current);
    } finally {
      setBusy(false);
    }
  };

  if (permission === 'denied') {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid="push-blocked">
        <p className="text-sm text-stone-900 flex items-center gap-2">
          <BellOff size={16} className="text-stone-500" /> Notifications are blocked
        </p>
        <p className="text-xs text-stone-500 mt-1">
          {inBrowser
            ? 'Click the lock icon to the left of the web address → Notifications → Allow, then refresh this page.'
            : 'Enable them in iOS Settings → Notifications → RxGRIT.'}
        </p>
      </div>
    );
  }

  return (
    <Button
      onClick={handleToggle}
      disabled={busy}
      variant={isSubscribed ? 'outline' : 'default'}
      className={
        isSubscribed
          ? 'border-stone-300 text-stone-700 hover:text-stone-900'
          : 'bg-[#EFB342] text-black hover:bg-[#D99F30]'
      }
      data-testid="push-toggle-btn"
    >
      {busy ? (
        <Loader2 className="animate-spin mr-2" size={16} />
      ) : isSubscribed ? (
        <Bell size={16} className="mr-2" />
      ) : (
        <BellOff size={16} className="mr-2" />
      )}
      {isSubscribed
        ? (inBrowser ? 'Pop-up alerts on (click to turn off)' : 'Notifications enabled')
        : (inBrowser ? 'Turn on pop-up alerts in this browser' : 'Enable home-screen notifications')}
    </Button>
  );
};

export default EnableNotificationsButton;
